<template>
  <Transition enter-active-class="transition ease-out duration-300" enter-from-class="opacity-0" enter-to-class="opacity-100" leave-active-class="transition ease-in duration-300" leave-from-class="opacity-100" leave-to-class="opacity-0">
    <div v-if="show" class="fixed inset-0 z-[9999] overflow-y-auto">
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm z-[9999]" @click="handleBackdropClick"></div>

      <!-- Modal -->
      <div class="flex min-h-full items-center justify-center p-4 min-w-full relative z-[10000]">
        <Transition
          enter-active-class="transition ease-out duration-300"
          enter-from-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to-class="opacity-100 translate-y-0 sm:scale-100"
          leave-active-class="transition ease-in duration-200"
          leave-from-class="opacity-100 translate-y-0 sm:scale-100"
          leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
          <div
            v-if="show"
            class="relative transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all w-full z-[10000]"
            :class="{
              'sm:max-w-lg': size === 'default',
              'sm:max-w-xl': size === 'lg',
              'sm:max-w-2xl': size === 'xl',
              'sm:max-w-4xl': size === '2xl',
              'sm:max-w-6xl': size === '3xl',
              'sm:max-w-7xl': size === '4xl',
              'sm:max-w-8xl': size === '5xl',
              'sm:max-w-9xl': size === '6xl',
              'sm:max-w-10xl': size === '7xl',
              'sm:max-w-11xl': size === '8xl',
              'sm:max-w-12xl': size === '9xl',
              'sm:max-w-13xl': size === '10xl',
            }">
            <slot></slot>
          </div>
        </Transition>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { watch, onUnmounted } from "vue"

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  size: {
    type: String,
    default: "default",
    validator: (value) => ["default", "lg", "xl", "2xl", "3xl", "4xl", "5xl", "6xl", "7xl", "8xl", "9xl", "10xl"].includes(value),
  },
  backdropExits: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(["close"])

const { registerModalOpen, registerModalClose } = useZIndex()

// Watch for show prop changes to toggle body scroll and register modal state
watch(
  () => props.show,
  (isOpen) => {
    if (process.client) {
      if (isOpen) {
        document.body.style.overflow = "hidden"
        registerModalOpen()
      } else {
        document.body.style.overflow = ""
        registerModalClose()
      }
    }
  },
  { immediate: true }
)

// Clean up when component is unmounted
onUnmounted(() => {
  if (process.client) {
    document.body.style.overflow = ""
    if (props.show) {
      registerModalClose()
    }
  }
})

const handleBackdropClick = (event) => {
  // Only close if clicking the backdrop
  if (event.target === event.currentTarget && props.backdropExits) {
    emit("close")
  }
}
</script>

<style scoped>
.modal-open {
  overflow: hidden;
}
</style>
